import type { ComponentType, ElementType } from "react";
import { authors, getAuthor, type Author } from "../data/authors";
import { site } from "../data/site";
import { dateOnly, isFutureDate } from "./date";
import { splitFrontmatter } from "./frontmatter";
import { authorPath, categoryPath, postPath, slugify, tagPath } from "./slug";

type MDXComponent = ComponentType<{ components?: Record<string, ElementType> }>;

type PostModule = {
  default: MDXComponent;
};

export type TocItem = {
  id: string;
  text: string;
  level: 2 | 3;
};

export type Post = {
  title: string;
  slug: string;
  excerpt: string;
  publishedAt: string;
  updatedAt: string;
  author: string;
  category: string;
  tags: string[];
  featuredImage: string;
  status: "published" | "draft";
  draft: boolean;
  sourcePath: string;
  url: string;
  absoluteUrl: string;
  categorySlug: string;
  tagSlugs: string[];
  authorUrl: string;
  categoryUrl: string;
  tagUrls: string[];
  authorProfile: Author;
  toc: TocItem[];
  paragraphCount: number;
  body: string;
  Component: MDXComponent;
};

const contentModules = import.meta.glob("../content/posts/*.mdx", {
  eager: true
}) as Record<string, PostModule>;

const rawModules = import.meta.glob("../content/posts/*.mdx", {
  eager: true,
  query: "?raw",
  import: "default"
}) as Record<string, string | { default: string }>;

function asString(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function asBoolean(value: unknown, fallback = false) {
  return typeof value === "boolean" ? value : fallback;
}

function asStringArray(value: unknown) {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function stripMarkdown(value: string) {
  return value
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_~]/g, "")
    .replace(/<[^>]+>/g, "")
    .trim();
}

export function extractToc(body: string): TocItem[] {
  const toc: TocItem[] = [];
  let insideFence = false;

  for (const line of body.split("\n")) {
    if (line.trim().startsWith("```")) {
      insideFence = !insideFence;
      continue;
    }

    if (insideFence) continue;

    const heading = line.match(/^(#{2,3})\s+(.+)$/);
    if (!heading) continue;

    const text = stripMarkdown(heading[2]);
    toc.push({
      id: slugify(text),
      text,
      level: heading[1].length as 2 | 3
    });
  }

  return toc;
}

export function countParagraphs(body: string) {
  return body
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter((block) => {
      if (!block) return false;
      if (/^(#{1,6}\s|```|~~~|import\s|export\s|<|>|[-*+]\s|\d+\.\s|\|)/.test(block)) return false;
      if (/^!\[[^\]]*]/.test(block)) return false;
      return true;
    }).length;
}

function buildPost(path: string, module: PostModule): Post {
  const rawEntry = rawModules[path];
  const raw = typeof rawEntry === "string" ? rawEntry : rawEntry.default;
  const { frontmatter, body } = splitFrontmatter(raw);
  const slug = asString(frontmatter.slug, path.split("/").pop()?.replace(/\.mdx$/, "") ?? "");
  const category = asString(frontmatter.category, "Knowledge Base");
  const tags = asStringArray(frontmatter.tags);
  const authorSlug = asString(frontmatter.author, authors[0].slug);
  const authorProfile = getAuthor(authorSlug) ?? authors[0];
  const url = postPath(slug);

  return {
    title: asString(frontmatter.title, slug),
    slug,
    excerpt: asString(frontmatter.excerpt),
    publishedAt: asString(frontmatter.publishedAt),
    updatedAt: asString(frontmatter.updatedAt, asString(frontmatter.publishedAt)),
    author: authorSlug,
    category,
    tags,
    featuredImage: asString(frontmatter.featuredImage, site.defaultImage),
    status: asString(frontmatter.status, "published") === "draft" ? "draft" : "published",
    draft: asBoolean(frontmatter.draft, false),
    sourcePath: path,
    url,
    absoluteUrl: `${site.url}${url}`,
    categorySlug: slugify(category),
    tagSlugs: tags.map(slugify),
    authorUrl: authorPath(authorProfile.slug),
    categoryUrl: categoryPath(category),
    tagUrls: tags.map(tagPath),
    authorProfile,
    toc: extractToc(body),
    paragraphCount: countParagraphs(body),
    body,
    Component: module.default
  };
}

export const posts: Post[] = Object.entries(contentModules)
  .map(([path, module]) => buildPost(path, module))
  .sort((a, b) => dateOnly(b.publishedAt).localeCompare(dateOnly(a.publishedAt)));

export function isPublished(post: Post, now = new Date()) {
  return post.draft !== true && post.status === "published" && !isFutureDate(post.publishedAt, now);
}

export function getPublishedPosts(now = new Date()) {
  return posts.filter((post) => isPublished(post, now));
}

export function getPostBySlug(slug: string) {
  return posts.find((post) => post.slug === slug);
}

export function isPreviewEnabled(search: string) {
  return import.meta.env.DEV && new URLSearchParams(search).get("preview") === "true";
}

export function getRelatedPosts(post: Post, limit = 4) {
  const scoredPosts = getPublishedPosts()
    .filter((candidate) => candidate.slug !== post.slug)
    .map((candidate) => {
      const categoryScore = candidate.categorySlug === post.categorySlug ? 3 : 0;
      const tagScore = candidate.tagSlugs.filter((tag) => post.tagSlugs.includes(tag)).length;
      return { post: candidate, score: categoryScore + tagScore };
    });

  const matchingPosts = scoredPosts
    .filter((candidate) => candidate.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((candidate) => candidate.post);

  if (matchingPosts.length > 0) {
    return matchingPosts.slice(0, limit);
  }

  return scoredPosts.slice(0, limit).map((candidate) => candidate.post);
}
